import { apiClient } from '../apiClient';
import type { ApiResponse } from '../http/types';
import type {
  BureauAlarm,
  BureauCategoryResponse,
  PagedBureauResponse,
  BureauStats,
  UltraCriticalAlarmsResponse,
  LocalAcknowledgePayload,
  AlarmPathStatePayload,
  MergeResult,
  BureauCustomersAlarmsResponse,
} from './bureauManagement.types';

const base = '/api/bureau-management';

// Simple in-memory cache to reduce repeated category fetches (helps dashboard cards)
type CacheEntry = {
  ts: number;
  res: Promise<ApiResponse<BureauCategoryResponse>> | ApiResponse<BureauCategoryResponse>;
};
const CATEGORY_CACHE_TTL_MS = 60 * 1000; // 60 seconds
const categoryCache: Record<string, CacheEntry> = {};
const HTTP_NOT_FOUND = 404;

function normalizeBureauAlarm(alarm: BureauAlarm): BureauAlarm {
  const rawAlarm = alarm as BureauAlarm & Record<string, unknown>;
  const localAckAt = [rawAlarm.ackDate, rawAlarm.AckDate, rawAlarm.ackDateTime, rawAlarm.AckDateTime,
    rawAlarm.localAckAt, rawAlarm.LocalAckAt, rawAlarm.acknowledgedAt,
    ...Object.entries(rawAlarm)
      .filter(([key]) => ['ackdate', 'ack_date', 'ackdatetime', 'ack_date_time', 'localackat', 'local_ack_at', 'acknowledgedat', 'acknowledged_at'].includes(key.toLowerCase()))
      .map(([, value]) => value)]
    .find(value => value !== undefined && value !== null && value !== '');
  return localAckAt === undefined
    ? alarm
    : { ...alarm, localAckAt: localAckAt as string | number };
}

function emptyUltraCriticalResponse(): ApiResponse<UltraCriticalAlarmsResponse | BureauAlarm[]> {
  return {
    success: true,
    data: {
      alarms: [],
      totalCount: 0,
      activeNow: 0,
      newLast15Minutes: 0,
      sitesAffected: 0,
    },
  };
}

function ultraCriticalErrorResponse(message: string, status?: number): ApiResponse<UltraCriticalAlarmsResponse | BureauAlarm[]> {
  return {
    success: false,
    data: null,
    error: {
      status,
      message,
    },
  };
}

function normalizeUltraCriticalResponse(
  res: ApiResponse<UltraCriticalAlarmsResponse | BureauAlarm[]>,
): ApiResponse<UltraCriticalAlarmsResponse | BureauAlarm[]> {
  if (!res.success && res.error?.status === HTTP_NOT_FOUND) {
    return emptyUltraCriticalResponse();
  }
  return res;
}

// GET /api/bureau-management/alarms
export function getAllAlarms(params?: { page?: number; pageSize?: number }): Promise<ApiResponse<PagedBureauResponse>> {
  return apiClient.get<PagedBureauResponse>(`${base}/alarms`, { params });
}

// GET /api/bureau-management/sites/{siteIdentifierRegistryId}/alarms
export function getAlarmsBySite(
  siteIdentifierRegistryId: string | number,
  params?: { page?: number; pageSize?: number; category?: string; sortBy?: string; sortDirection?: 'asc' | 'desc'; search?: string }
): Promise<ApiResponse<PagedBureauResponse>> {
  return apiClient.get<PagedBureauResponse>(`${base}/sites/${encodeURIComponent(siteIdentifierRegistryId)}/alarms`, { params });
}


function mergeParallelResponses(
  responses: ApiResponse<BureauCategoryResponse>[],
  _validCategories: string[],
): MergeResult {
  const allAlarms: BureauAlarm[] = [];
  let serverTotalCount = 0;
  const categoryCounts: Record<string, number> = {};
  const perCategoryCounts: number[] = [];

  for (let i = 0; i < responses.length; i++) {
    const res = responses[i];
    if (res?.success && res.data) {
      const alarms = res.data.alarms || res.data.bureauAlarms || [];
      const count = res.data.totalRecords ?? res.data.totalCount ?? 0;
      perCategoryCounts[i] = count;
      allAlarms.push(...alarms.map(normalizeBureauAlarm));
      serverTotalCount += count;
      if (res.data.categoryCounts) {
        Object.assign(categoryCounts, res.data.categoryCounts);
      }
    } else {
      perCategoryCounts[i] = 0;
    }
  }
  return { allAlarms, serverTotalCount, categoryCounts, perCategoryCounts };
}

function fillMissingCategoryCounts(
  categoryCounts: Record<string, number>,
  validCategories: string[],
  perCategoryCounts: number[],
): void {
  for (let i = 0; i < validCategories.length; i++) {
    const cat = validCategories[i];
    if (categoryCounts[cat] === undefined) {
      categoryCounts[cat] = perCategoryCounts[i] || 0;
    }
  }
}

async function fetchCategoryPages(
  cat: string,
  expected: number,
  options?: { sortBy?: string; sortDirection?: 'asc' | 'desc' }
): Promise<BureauAlarm[]> {
  const PAGE_SIZE = 100;
  const sortBy = options?.sortBy;
  const sortDirection = options?.sortDirection;
  let sortQuery = '';
  if (sortBy) {
    sortQuery += `&sortBy=${encodeURIComponent(sortBy)}`;
  }
  if (sortDirection) {
    sortQuery += `&sortDirection=${encodeURIComponent(sortDirection)}`;
  }
  let pageNum = 1;
  const collected: BureauAlarm[] = [];
  while (collected.length < expected) {
    const fetchUrl = `${base}/categories/${encodeURIComponent(cat)}/alarms?page=${pageNum}&pageSize=${PAGE_SIZE}${sortQuery}`;
    // eslint-disable-next-line no-await-in-loop
    const fr = await apiClient.get<BureauCategoryResponse>(fetchUrl).catch(() => null);
    if (!fr?.success || !fr.data) break;
    const alarms = fr.data.alarms || fr.data.bureauAlarms || [];
    if (alarms.length === 0) break;
    collected.push(...alarms.map(normalizeBureauAlarm));
    if (alarms.length < PAGE_SIZE) break;
    pageNum += 1;
  }
  return collected;
}

async function fetchAllCategoryPages(
  validCategories: string[],
  perCategoryCounts: number[],
  options?: { sortBy?: string; sortDirection?: 'asc' | 'desc' },
): Promise<BureauAlarm[]> {
  const fullyMerged: BureauAlarm[] = [];
  for (let i = 0; i < validCategories.length; i++) {
    const expected = perCategoryCounts[i] || 0;
    if (expected <= 0) {continue;}
    // eslint-disable-next-line no-await-in-loop
    const fetched = await fetchCategoryPages(validCategories[i], expected, options);
    fullyMerged.push(...fetched);
    if (fetched.length < expected) {
      console.info(`[bureauManagement] Warning: category ${validCategories[i]} expected ${expected} but fetched ${fetched.length}`);
    }
  }
  return fullyMerged;
}

function buildCategoryResult(
  allAlarms: BureauAlarm[],
  page: number,
  pageSize: number,
  categoryCounts: Record<string, number>,
  fetchAll: boolean,
): ApiResponse<BureauCategoryResponse> {
  const finalCount = allAlarms.length;
  const totalPages = Math.max(1, Math.ceil(finalCount / pageSize));
  const start = (page - 1) * pageSize;
  const data: BureauCategoryResponse = {
    alarms: allAlarms.slice(start, start + pageSize),
    bureauAlarms: allAlarms.slice(start, start + pageSize),
    totalRecords: finalCount,
    totalCount: finalCount,
    totalPages,
    page,
    pageSize,
    categoryCounts,
  };
  if (fetchAll) {
    data.alarmsAll = allAlarms;
    data.bureauAlarmsAll = allAlarms;
  }
  return { success: true, data };
}

export function getBureauAlarmsByCategory(
  category: string,
  page = 1,
  pageSize = 50,
  fetchAll = false,
  options?: { sortBy?: string; sortDirection?: 'asc' | 'desc' }
): Promise<ApiResponse<BureauCategoryResponse>> {
  const sortBy = options?.sortBy;
  const sortDirection = options?.sortDirection;
  const cacheKey = `${category}|${page}|${pageSize}|${fetchAll}|${sortBy || ''}|${sortDirection || ''}`;
  // Only cache explicit full-fetch scenarios to avoid stale monitor grid data.
  const shouldUseCache = fetchAll;
  if (shouldUseCache) {
    const cached = categoryCache[cacheKey];
    if (cached && (Date.now() - cached.ts) < CATEGORY_CACHE_TTL_MS) {
      return Promise.resolve(cached.res as ApiResponse<BureauCategoryResponse>);
    }
  }

  const sortQuery = `${sortBy ? `&sortBy=${encodeURIComponent(sortBy)}` : ''}${sortDirection ? `&sortDirection=${encodeURIComponent(sortDirection)}` : ''}`;

  if (category === 'all') {
    const validCategories = ['active-not-ack', 'active-ack', 'not-active-not-ack'];
    return Promise.all(
      validCategories.map(cat =>
        apiClient.get<BureauCategoryResponse>(`${base}/categories/${encodeURIComponent(cat)}/alarms?page=${page}&pageSize=${pageSize}${sortQuery}`)
      )
    ).then(async responses => {
      const { allAlarms, serverTotalCount, categoryCounts, perCategoryCounts } = mergeParallelResponses(responses, validCategories);
      fillMissingCategoryCounts(categoryCounts, validCategories, perCategoryCounts);
      if (fetchAll && allAlarms.length < serverTotalCount) {
        try {
          const fullAlarms = await fetchAllCategoryPages(validCategories, perCategoryCounts, {
            sortBy,
            sortDirection,
          });
          allAlarms.length = 0;
          allAlarms.push(...fullAlarms);
        } catch (err) {
          console.info('[bureauManagement] Full fetch for "all" failed, using partial merge:', err);
        }
      }
      return buildCategoryResult(allAlarms, page, pageSize, categoryCounts, fetchAll);
    }).catch(error => {
      console.info('[bureauManagement] Error fetching all categories:', error);
      const out = {
        success: false,
        error: { message: (error as Error)?.message || 'Failed to fetch all categories' },
      } as ApiResponse<BureauCategoryResponse>;
      if (shouldUseCache) {
        categoryCache[cacheKey] = { ts: Date.now(), res: out };
      }
      return out;
    });
  }

  const url = `${base}/categories/${encodeURIComponent(category)}/alarms?page=${page}&pageSize=${pageSize}${sortQuery}`;
  const resultPromise = apiClient.get<BureauCategoryResponse>(url)
    .then(async res => {
      if (!res.success && res.error && res.error.status === 404) {
        return {
          success: true,
          data: { alarms: [], bureauAlarms: [], totalRecords: 0, totalCount: 0, page, pageSize, totalPages: 0, category, categoryCounts: {} },
        } as ApiResponse<BureauCategoryResponse>;
      }
      if (res.success && res.data) {
        const alarms = (res.data.alarms || res.data.bureauAlarms || []).map(normalizeBureauAlarm);
        const totalCount = res.data.totalRecords ?? res.data.totalCount ?? 0;

        // Fetch all pages only when explicitly requested.
        if (fetchAll && alarms.length < totalCount && pageSize >= 10) {
          try {
            const allAlarms = await fetchCategoryPages(category, totalCount, {
              sortBy,
              sortDirection,
            });
            if (allAlarms.length > alarms.length) {
              const fullCount = allAlarms.length;
              const result: ApiResponse<BureauCategoryResponse> = {
                ...res,
                data: {
                  ...res.data,
                  alarms: allAlarms,
                  bureauAlarms: allAlarms,
                  totalRecords: fullCount,
                  totalCount: fullCount,
                  totalPages: Math.max(1, Math.ceil(fullCount / pageSize)),
                },
              };
              if (shouldUseCache) {
                categoryCache[cacheKey] = { ts: Date.now(), res: result };
              }
              return result;
            }
          } catch (err) {
            console.info('[bureauManagement] Full fetch failed, using partial result for', category, ':', err);
          }
        }

        return { ...res, data: { ...res.data, alarms, bureauAlarms: alarms, totalRecords: totalCount, totalCount } } as ApiResponse<BureauCategoryResponse>;
      }
      if (shouldUseCache) {
        categoryCache[cacheKey] = { ts: Date.now(), res };
      }
      return res;
    })
    .catch(error => {
      console.info('[bureauManagement] Error for', category, ':', error);
      const out = { success: false, error: { message: (error as Error)?.message || 'Failed to fetch category' } } as ApiResponse<BureauCategoryResponse>;
      if (shouldUseCache) {
        categoryCache[cacheKey] = { ts: Date.now(), res: out };
      }
      return out;
    });
  if (shouldUseCache) {
    categoryCache[cacheKey] = { ts: Date.now(), res: resultPromise };
  }
  return resultPromise;
}

// Backwards-compatible helpers (migrated from the older misspelled service)
export function getByCategory(category: string | number, params?: { page?: number; pageSize?: number }): Promise<ApiResponse<PagedBureauResponse>> {
  return apiClient.get<PagedBureauResponse>(`${base}/categories/${encodeURIComponent(String(category))}/alarms`, { params });
}

export function getUnacknowledged(params?: { page?: number; pageSize?: number }): Promise<ApiResponse<PagedBureauResponse>> {
  return apiClient.get<PagedBureauResponse>(`${base}/unacknowledged`, { params });
}

// GET /api/bureau-management/alarms/ultra-critical
export function getUltraCriticalAlarms(
  params?: { page?: number; pageSize?: number; category?: string; includeToBeRemoved?: boolean }
): Promise<ApiResponse<UltraCriticalAlarmsResponse | BureauAlarm[]>> {
  const requestParams = {
    page: 1,
    pageSize: 500,
    includeToBeRemoved: true,
    ...(params || {}),
  };

  return apiClient
    .get<UltraCriticalAlarmsResponse | BureauAlarm[]>(`${base}/alarms/ultra-critical`, { params: requestParams })
    .then(res => normalizeUltraCriticalResponse(res))
    .catch(error => ultraCriticalErrorResponse((error as Error)?.message || 'Failed to load ultra critical alarms'));
}

// PATCH /api/bureau-management/alarms/{bureauAlarmId}/local-acknowledge
export function localAcknowledge(bureauAlarmId: string | number, payload?: LocalAcknowledgePayload): Promise<ApiResponse<BureauAlarm>> {
  return apiClient.patch<BureauAlarm>(`${base}/alarms/${bureauAlarmId}/local-acknowledge`, payload);
}

// GET /api/bureau-management/stats
export function getStats(): Promise<ApiResponse<BureauStats>> {
  return apiClient.get<BureauStats>(`${base}/stats`);
}

// GET /api/bureau-management/active-sites/stats
export function getActiveSitesStats(): Promise<ApiResponse<BureauStats>> {
  return apiClient.get<BureauStats>(`${base}/active-sites/stats`);
}

// PUT /api/bureau-management/alarm-paths/{alarmPathId}/state
export function setAlarmPathState(alarmPathId: string | number, payload?: AlarmPathStatePayload): Promise<ApiResponse<BureauAlarm>> {
  return apiClient.put<BureauAlarm>(`${base}/alarm-paths/${alarmPathId}/state`, payload);
}

// GET /api/bureau-management/alarms/{bureauAlarmId}
export function getById(bureauAlarmId: string | number): Promise<ApiResponse<BureauAlarm>> {
  return apiClient.get<BureauAlarm>(`${base}/alarms/${bureauAlarmId}`);
}

// GET /api/bureau-management/customers/alarms - Tree of customers and sites with alarm counts
// Call with no query parameters to get all data
export function getCustomersWithAlarms(params?: { category?: string; includeToBeRemoved?: boolean; includeAllSites?: boolean }): Promise<ApiResponse<BureauCustomersAlarmsResponse>> {
  return apiClient.get<BureauCustomersAlarmsResponse>(`${base}/customers/alarms`, { params });
}

export function getAlarmsByCustomer(
  customerId: string | number,
  params?: { page?: number; pageSize?: number; category?: string; sortBy?: string; sortDirection?: 'asc' | 'desc'; search?: string }
): Promise<ApiResponse<PagedBureauResponse>> {
  return apiClient.get<PagedBureauResponse>(`${base}/customers/${encodeURIComponent(String(customerId))}/alarms`, { params });
}

export default {
  getAllAlarms,
  getAlarmsBySite,
  getBureauAlarmsByCategory,
  getByCategory,
  getUnacknowledged,
  getUltraCriticalAlarms,
  localAcknowledge,
  getStats,
  getActiveSitesStats,
  setAlarmPathState,
  getById,
  getCustomersWithAlarms,
  getAlarmsByCustomer,
};
